import type {
  AsidePanelFacetsMessages,
  AsidePanelMessages,
  BaseToolMessages,
  DefaultFieldsMessages,
  Dictionary,
  ToolingPreviewMessages,
  ToolsMessages,
} from '../models'
import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum'
import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'
import { PlayErrorReasonEnum } from '@empathybroker/tsclient-play/dist/enums/play-error-reason.enum'
import { PromotionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/promotion-type.enum'
import {
  HiddenElementsAction,
  PinProductAction,
  QueryRankingElementRankingAction,
} from '@empathyco/playboard-core-domain'

import {
  CatalogFieldTypeEnum,
  FacetSortTypeEnum,
  LocaleEnum,
  RelatedTagPositionEnum,
  RestrictedValueEnum,
  ResultMappingIdEnum,
} from '../enums'
import { APP_MESSAGES } from './app.messages'

const DEFAULT_FIELDS_MESSAGES_EN: DefaultFieldsMessages = {
  id: {
    label: 'ID',
  },
  name: {
    label: 'Name',
  },
  creationDate: {
    label: 'Created on',
  },
  updateDate: {
    label: 'Updated on',
  },
  user: {
    label: 'Updated by',
  },
  enabled: {
    label: 'Status',
    states: {
      enabled: 'On',
      disabled: 'Off',
    },
  },
  schedule: {
    label: 'Scheduled',
    states: {
      schedule: 'Scheduled',
      activationDate: 'From {date}',
      deactivationDate: 'to {date}',
      always: 'Always active',
    },
  },
  placeholders: {
    label: '', // TODO: remove placeholders from FIELD_MESSAGES
    always: 'Always',
    scheduled: 'Select a date',
    keywords: {
      active: "Add words separated by ';' or ',' and press the \"enter\" key",
      disabled: 'Queries addition is off for this link',
    },
  },
  activationDate: {
    label: 'Active from',
  },
  deactivationDate: {
    label: 'Active to',
  },
}

const TOOLING_PREVIEW_MESSAGES_EN: ToolingPreviewMessages = {
  title: 'Preview',
  search: 'Type a query',
  filters: {
    title: 'Select preview filters',
    subtitle:
      'If you select preview filters you can see how users will view the result grid with there filters applied.',
    accept: 'Apply filters',
  },
  results: '{counter} results',
  noDataFound: {
    title: "D'oh!",
    description:
      'No results found. Please try adjusting the preview filters to see if you get different results.',
  },
  emptyQuerySearch: {
    text1: 'Type a query and select preview filter',
    text2: 'to start the products preview.',
  },
}

const BASE_ASIDE_MESSAGES_EN: Partial<AsidePanelMessages> = {
  actions: {
    clear: 'Clear all',
    accept: 'Save',
    cancel: 'Cancel',
    add: 'Add',
  },
}

const BASE_ASIDE_FACETS_MESSAGES_EN: AsidePanelFacetsMessages = {
  sortType: {
    values: {
      [FacetSortTypeEnum.ALPHABETICALLY]: 'Alphabetically',
      [FacetSortTypeEnum.RESULTS_NUMBER]: 'Results number',
      custom: 'Custom',
    },
  },
  labels: {
    facets: 'Facet',
    name: 'Name',
    sortValues: 'Values sorting',
    unit: 'Unit',
    decimalDigits: 'Decimal digits',
  },
  metadata: {
    title: 'metadata',
    description: 'Add key-value pairs to the metadata of the facet being configured',
    labels: {
      field: 'Key',
      value: 'Value',
    },
    addValue: 'Add',
    editValue: 'Save changes',
    error: 'This metadata configuration already exists',
  },
}

const CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN =
  'changes will be applied on the next catalogue indexing cycle'

export const getBaseToolMessagesEN = (
  toolName = { singular: 'Configuration', plural: 'Configurations' },
  indexingWarn = false,
): BaseToolMessages => {
  const indexingWarnMessage = indexingWarn ? `; ${CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN}` : ''
  return {
    actions: {
      accept: 'Save',
      cancel: 'Cancel',
      exit: 'Back',
    },
    noDataFound: `We couldn’t find any ${toolName.plural.toLowerCase()} that match your search.`,
    dataFiltered: ` Try removing some filters and searching again.`,
    deleteMany: {
      confirmTitle: `Delete ${toolName.plural.toLowerCase()}`,
      confirmMessage: 'Are you sure you want to delete the selected items ({itemsCount})?',
      success: `${toolName.plural} deleted successfully${indexingWarnMessage}`,
    },
    deleteOne: {
      confirmTitle: `Delete ${toolName.singular.toLowerCase()}`,
      confirmMessage: 'Are you sure you want to delete the selected item?',
      success: `${toolName.singular} deleted successfully${indexingWarnMessage}`,
    },
    sections: {
      details: {
        title: 'Details',
      },
      filters: {
        title: 'Filters',
        info: 'Select the filter data you want to apply to create a custom scenario for your configuration',
      },
      settings: {
        title: 'Settings',
      },
      preview: {
        title: 'Preview',
      },
    },
    errors: {
      enableConflictError: {
        title: 'Overlapping configurations found',
        message:
          'Some of the selected configurations could not be turned on because there are other configurations matching your selection (<strong>{conflictingElements}</strong>).',
      },
    },
    create: {
      success: `${toolName.singular} created successfully${indexingWarnMessage}`,
    },
    update: {
      success: `${toolName.singular} edited successfully${indexingWarnMessage}`,
    },
    enable: {
      success: `${toolName.plural} turned on successfully${indexingWarnMessage}`,
    },
    disable: {
      success: `${toolName.plural} turned off successfully${indexingWarnMessage}`,
    },
    repeatedContext: {
      dialog: {
        title: 'Overlapping configurations found',
        content:
          'You cannot save the current configuration and make it active because some conflicts are found. Here’s the list of overlapping configurations:',
        detail: ' with the queries: {queries}',
        options: {
          title: 'How would you like to proceed?',
          disablePrevious: 'Turn off these configurations and save the current one as turned on',
          disableActual: 'Save the current one as turned off',
        },
        confirm: 'Save',
        deny: 'Cancel',
      },
    },
  }
}

const APP_FEATURE_NAMES_EN = APP_MESSAGES[LocaleEnum.ENGLISH].app.features
const INDEX_BASE_MESSAGES_EN = getBaseToolMessagesEN({ singular: 'Index', plural: 'Indexes' })
const NEXT_QUERIES_BASE_MESSAGES_EN = getBaseToolMessagesEN({
  singular: APP_FEATURE_NAMES_EN.nextQueries,
  plural: APP_FEATURE_NAMES_EN.nextQueries,
})

const TOOLS_MESSAGES_EN: ToolsMessages = {
  equalize: {
    ...getBaseToolMessagesEN({
      singular: APP_FEATURE_NAMES_EN.equalize,
      plural: APP_FEATURE_NAMES_EN.equalize,
    }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      title: {
        label: 'Name',
        defaultDescription:
          'The configuration defined as default is configured through the Instance Management during setup. ' +
          'It is going to affect the results scoring if there’s any other Equalize configuration affecting for that context. ' +
          'If you require some changes please contact First Line Support. Those changes will apply enterprise wide.',
      },
      notes: {
        label: 'Notes',
        actions: {
          show: 'Show notes',
          hide: 'Hide notes',
        },
      },
      endpointId: {
        label: 'Endpoint',
        dialog: {
          title: 'Context change',
          content:
            'If you select another context, any changes for this configuration will be lost. Do you want to continue?',
          confirm: 'Continue',
          deny: 'Back',
        },
      },
      modified: {
        label: 'Modified',
      },
      enabledTime: {
        label: 'Active time',
      },
      enabled: {
        ...DEFAULT_FIELDS_MESSAGES_EN.enabled,
        alert: 'Enabling this equalize will change the current Search configuration',
      },
      search: {
        label: 'Search products to preview',
        help: 'Search a term to preview the equalize configuration result',
      },
    }),
    products: {
      countInfo: 'Showing {counter} results',
    },
    enable: {
      confirmTitle: 'Enable equalize',
      confirmMessage:
        'This action will turn off the current equalize configuration, are you sure you want to do it?',
      success: 'Equalize configuration turned on successfully',
    },
    features: {
      defaultButton: 'Default configuration',
      setByDefault: 'Set by default',
      multiMatchSearch: {
        title: 'Textual match',
        description:
          'Set up the weight for searchable product catalogue attributes where the search term is found to determine product ranking.',
      },
      semanticMatchSearch: {
        title: 'Semantic match',
        description:
          'Set up the weight for the vector enrichment source used to enhance the searchable product catalogue attributes where the search term is found to determine product ranking.',
        disabledDescription:
          'This scoring criterion isn’t available. Contact your Key Account Manager to make the necessary changes.',
      },
      boosting: {
        title: 'Global business rules',
        description:
          'Set up ranking values for given product catalogue attributes met by product results to boost or demote their relevance and enhance product ranking.',
        audit: {
          field: 'Field',
          value: 'Value',
          action: 'Action',
          boostFactor: 'Boost factor',
        },
      },
      functionScore: {
        title: 'Function score',
        description:
          'Set up ranking values for customized KPI’s to increase or decrease the product scoring.',
        audit: {
          fieldName: 'Field name',
          formulaType: 'Formula type',
          weight: 'Weight',
          boostFactor: 'Boost factor',
        },
      },
      sponsoredScore: {
        title: 'Monetization',
        description:
          'Set up the weight for the monetization attribute in your product catalogue to enhance the relevance of monetized products that match the query, boosting their visibility on the results.',
        disabledDescription:
          'This scoring criterion isn’t available. Contact your Key Account Manager to make the necessary changes.',
        audit: {
          fieldName: 'Field name',
          formulaType: 'Formula type',
          weight: 'Weight',
          boostFactor: 'Boost factor',
        },
      },
    },
    details: 'Details',
    configuration: 'Configuration',
    fieldValues: 'Fields',
    results: '{counter} results',
    conflictErrorList: {
      title: 'Warning',
      message: `There\'s a conflict with {ids}`,
    },
    conflictErrorForm: {
      title: 'Warning',
      message:
        'An Equalize configuration with <strong>{id}</strong> is already exists for this scenario. The new configuration will be turned off and saved automatically.',
      continue: 'Do you want to continue?',
    },
    preview: {
      ...TOOLING_PREVIEW_MESSAGES_EN,
      browseField: {
        label: 'Attribute',
      },
      browseValue: {
        label: 'Attribute value',
        filters: {
          title: 'Select attribute filters',
          subtitle: 'These filters are only used to load attribute values.',
          accept: 'Apply filters',
        },
        errors: {
          noValues: 'There are no values available for this attribute',
        },
      },
    },
  },
  synonyms: {
    ...getBaseToolMessagesEN({ singular: 'Synonyms', plural: 'Synonyms' }, true),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      hyponyms: {
        label: 'One way Synonyms',
        placeholder: 'Add one way synonyms',
        help: 'Each of these terms will return the same as its equivalent synonyms',
        valuesCountInfo: '{counter} hyponyms added',
      },
      synonyms: {
        label: 'Synonyms',
        placeholder: 'Add synonyms',
        help: 'All these terms will mean the same for the Search engine',
        valuesCountInfo: '{counter} synonyms added',
        errors: {
          [PlayErrorReasonEnum.DUPLICATED]: 'Conflicting values ({values}) on synonym [{synonym}]',
        },
      },
      type: {
        label: 'Type',
        values: {
          color: 'Color',
          basic: 'Basic',
        },
      },
    }),
    noValues: 'No synonyms added yet',
    return: 'Returns also',
    conflicts: {
      disable: {
        success: `Conflicting synonym turned off successfully; ${CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN}`,
      },
      delete: {
        confirmTitle: 'Delete synonym',
        confirmMessage: 'Are you sure you want to delete the conflicting synonym?',
        success: `Conflicting synonym deleted successfully; ${CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_EN}`,
      },
    },
    suggestions: {
      title: 'Shoppers also searched for… ',
      help: 'Add as a synonym related search terms that shoppers usually search for.',
    },
    reminder: {
      heuristic:
        'Reminder: at this time, synonyms will only apply to queries diverted to heuristic.',
    },
  },
  blacklist: {
    ...getBaseToolMessagesEN({ singular: APP_FEATURE_NAMES_EN.blacklist, plural: 'Blacklists' }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      lang: {
        label: 'Language',
        errors: {
          [PlayErrorReasonEnum.INVALID_FIELD]:
            'There is already a blacklist configuration on for this language',
        },
      },
      terms: {
        label: 'Terms',
        info: 'List of terms associated with the blacklist',
      },
    }),
  },
  promotions: {
    ...getBaseToolMessagesEN({ singular: APP_FEATURE_NAMES_EN.promotion, plural: 'Promotions' }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      name: {
        label: 'Name',
      },
      keywords: {
        label: 'Queries',
        info: 'List of search terms in which the promotion is shown',
        valuesCountInfo: '{counter} queries added',
      },
      matchAll: {
        label: 'Match all queries',
        value: 'All queries',
        help: 'This option will be applied to all searches of your site',
        enabled: 'On',
        disabled: 'Off',
      },
      clicks: {
        label: 'Clicks',
      },
      alertDevicesTOPs: {
        label: 'Redirect TOPs default to web, Shoppable TOPs default to all devices.',
      },
    }),
    aside: {
      ...BASE_ASIDE_MESSAGES_EN,
      header: 'Promotion settings',
      type: {
        label: 'Type',
        values: {
          [PromotionTypeEnum.BANNER]: 'Banner',
          [PromotionTypeEnum.PROMOTED]: 'Promoted',
        },
      },
      position: {
        label: 'Position',
        formLabel: {
          [PromotionTypeEnum.BANNER]: 'Row',
          [PromotionTypeEnum.PROMOTED]: 'Position',
        },
      },
      url: {
        label: 'URL',
        help: 'Add the promotion URL path. Use a relative URL (e.g. /mothersday) only if your implementation supports it.',
        error: 'URL cannot be empty.',
      },
      imageUrl: {
        label: 'Image URL',
        previewLabel: 'Image preview',
        help: 'Add the image URL path to be displayed in the SERP.',
        info: 'Add a second image URL path separated by a comma (e.g. “image1url,image2url”)',
        error: 'URL cannot be empty.',
      },
      title: {
        label: 'Title',
        errors: {
          empty: 'Title cannot be empty',
          length: 'Title is too long',
        },
      },
    },
    settings: {
      emptyCard: 'No title',
      error: 'There are errors inside this setting',
    },
    enable: {
      confirmTitle: 'Enable Promotion',
      confirmMessage:
        'This action will turn off the configurations for the selected promotions that already have one on. Do you want to continue?',
      success: 'Promotion configuration turned on successfully',
    },
    preview: {
      noResults: 'Oops! Something went wrong while loading the preview.',
      cardType: {
        desktop: 'Desktop',
        mobile: 'Mobile',
      },
    },
  },
  redirections: {
    ...getBaseToolMessagesEN({
      singular: APP_FEATURE_NAMES_EN.redirection,
      plural: 'Redirections',
    }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      keywords: {
        label: 'Queries',
        info: 'List of search terms in which the redirection is shown',
        valuesCountInfo: '{counter} queries added',
      },
      url: {
        label: 'URL',
        help: 'Add the redirection URL path. Use a relative URL (e.g. /mothersday) only if your implementation supports it.',
      },
    }),
  },
  configurations: {
    ...getBaseToolMessagesEN({
      singular: APP_FEATURE_NAMES_EN.configuration.browse,
      plural: 'Configurations',
    }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      action: {
        label: 'Action',
      },
      restricted: {
        label: 'Restricted',
        roles: {
          default: 'Everyone',
        },
        values: {
          [RestrictedValueEnum.READ]: '<b>READ:</b> {roles}',
          [RestrictedValueEnum.WRITE]: '<b>WRITE:</b> {roles}',
        },
      },
      endpointId: {
        label: 'Endpoint',
        values: {
          search: {
            label: 'Search',
          },
          browse: {
            label: 'Browse',
          },
        },
        dialog: {
          title: 'Context change',
          content:
            'If you select another context, any changes for this configuration will be lost. Do you want to continue?',
          confirm: 'Continue',
          deny: 'Back',
        },
      },
      queriesAttribute: {
        label: 'Queries/Attributes',
      },
      keywords: {
        label: 'Queries',
        info: 'List of search terms in which the configuration is applied',
      },
      browseField: {
        label: 'Attribute',
      },
      browseValue: {
        label: 'Attribute value',
        filters: {
          title: 'Select attribute filters',
          subtitle: 'These filters are only used to load attribute values.',
          accept: 'Apply filters',
        },
        errors: {
          noValues: 'There are no values available for this attribute',
        },
      },
      features: {
        label: 'Modified',
        attributes: 'attributes',
        hiddenAttributes: 'hidden attributes',
        products: 'products',
        pinnedProducts: 'pins',
        hiddenProducts: 'hidden products',
        facets: 'facets',
      },
      facets: {
        label: 'Facets',
        info: 'Select the group of facets and values to be visible on the products listing page.',
        edit: 'Select facets',
        id: {
          label: 'Facet',
        },
        prettyName: {
          label: 'Name',
        },
        sortType: {
          label: 'sort values',
          inlineLabel: 'sorted',
          values: {
            default: 'by default',
            [FacetSortTypeEnum.ALPHABETICALLY]: 'alphabetically',
            [FacetSortTypeEnum.RESULTS_NUMBER]: 'by results count',
          },
        },
        values: {
          empty: "There aren't any values configured.",
        },
        actions: {
          add: 'Add Facet',
          preview: 'Show facets',
        },
        emptyList: "There aren't any facets configured.",
      },
      attributes: {
        name: {
          label: 'Attribute',
        },
        value: {
          label: 'Value',
          filters: {
            title: 'Select attribute filters',
            subtitle: 'These filters are only used to load attribute values.',
            accept: 'Apply filters',
          },
          errors: {
            noValues: 'There are no values available for this attribute',
          },
        },
        label: 'Attribute Ranking',
        info: 'Boost and bury specific values of your catalog attributes.',
        edit: 'Select attributes',
        fieldName: 'Attribute',
        fieldValue: 'Value',
        noValues: 'There are no values available for this attribute',
        actions: {
          bury: 'Bury',
          boost: 'Boost',
        },
        emptyList: "There aren't any attributes configured.",
        setupRanking: 'Set up an Attribute',
        editRankingTitle: 'Edit attribute ranking',
      },
      hiddenAttributes: {
        label: 'Hidden Attributes',
        emptyList: "There aren't any hidden attributes.",
        setupRanking: 'Hide an attribute',
      },
      selectAllChild: {
        label: 'Select all child values',
        help: 'When enabled, the configuration will match all queries instead of specific keywords.',
        enabled: 'On',
        disabled: 'Off',
      },
      products: {
        label: 'Product Ranking',
        info: "Select directly in the preview the products you want to boost or bury. They'll be listed here.",
        emptyList: "There aren't any products configured.",
        setupRanking: 'Set up a Product',
        invalidProductID: 'Invalid product ID',
        search: {
          tag: 'Query',
          query: 'Search products',
          help: 'Search a product using a query or an ID',
        },
        notFound: 'No products found',
        editRankingTitle: 'Edit product ranking',
      },
      pinnedProducts: {
        label: 'Pinned Products',
        emptyList: "There aren't any pinned products.",
        setupRanking: 'Pin a product',
      },
      hiddenProducts: {
        label: 'Hidden Products',
        info: "Select directly in the preview the products you want to hide. They'll be listed here.",
        emptyList: "There aren't any hidden products.",
        setupRanking: 'Hide a product',
        invalidProductID: 'Invalid product ID',
        search: {
          tag: 'Query',
          query: 'Search products',
          help: 'Search a product using a query or an ID',
        },
        notFound: 'No products found',
        editRankingTitle: 'Edit hidden product',
      },
      inheritance: {
        label: 'Inheritance',
        inputLabel: 'Apply configuration to child items',
        states: {
          enabled: 'Yes',
          disabled: 'No',
        },
      },
      elements: {
        label: 'Elements',
      },
    }),
    enable: {
      confirmTitle: 'Enable Results Ranking',
      confirmMessage:
        'This action will turn off the configurations for the selected Results Ranking that already have one on, do you want to continue?',
      success: 'Results Ranking configuration turned on successfully',
    },
    attributes: {
      names: {
        category: 'categories',
      },
      noDataFound: 'No {attributeName} found',
    },
    configurations: 'configurations',
    results: '{counter} results',
    removeAll: {
      label: 'Remove All',
      title: 'Remove all configurations',
      description: 'Are you sure you want to remove all configurations in this section?',
      actions: {
        confirm: 'Remove',
        cancel: 'Cancel',
      },
    },
    modals: {
      attributes: {
        title: 'Attribute ranking',
        overlappedResult: 'Attribute with filter and/or schedule conflicts',
      },
      hiddenAttributes: {
        title: 'Hidden attribute',
        overlappedResult: 'Attribute with filter and/or schedule conflicts',
      },
      products: {
        title: 'Product ranking',
        fields: {
          products: {
            label: 'Products',
            placeholder: 'Type a product ID',
            description: 'Add multiple product IDs separated by semicolons and press Enter',
          },
        },
        overlappedResult: 'Product with filter and/or schedule conflicts',
      },
      pinnedProducts: {
        title: 'Pin product',
        info: 'Be aware that the final position of the product might be affected by other configurations',
        fields: {
          productId: {
            label: 'Product ID',
          },
          position: {
            label: 'Position',
            info: 'Choose a position between {min} and {max}',
          },
        },
        overlappedResult: 'Product with filter and/or schedule conflicts',
        positionInUse: 'Another product is pinned at this position',
      },
      hiddenProducts: {
        title: 'Hide product',
        fields: {
          products: {
            label: 'Products',
            placeholder: 'Type a product ID',
            description: 'Add multiple product IDs separated by semicolons and press Enter',
          },
        },
        overlappedResult: 'Product with filter and/or schedule conflicts',
      },
    },
    preview: TOOLING_PREVIEW_MESSAGES_EN,
    quickRanking: {
      title: 'Quick boost',
      resultsLabel: 'Type a product ID',
      resultsDescription: 'Add multiple product IDs separated by semicolons and press Enter',
      actions: {
        [QueryRankingElementRankingAction.BOOST]: 'Boost',
        [QueryRankingElementRankingAction.BURY]: 'Bury',
      },
      overlappedResult: 'Product with filter and/or schedule conflicts',
    },
    status: {
      [QueryRankingElementRankingAction.BOOST]: 'Boosted',
      [QueryRankingElementRankingAction.BURY]: 'Buried',
      [PinProductAction]: 'Pinned',
      [HiddenElementsAction]: 'Hidden',
    },
    actionLabels: {
      [QueryRankingElementRankingAction.BURY]: {
        positive: 'Bury',
        positivePast: 'Buried',
        negative: 'Unbury',
      },
      [QueryRankingElementRankingAction.BOOST]: {
        positive: 'Boost',
        positivePast: 'Boosted',
        negative: 'Unboost',
      },
      [PinProductAction]: {
        positive: 'Pin',
        positivePast: 'Pinned',
        negative: 'Unpin',
      },
      [HiddenElementsAction]: {
        positive: 'Hide',
        positivePast: 'Hidden',
        negative: 'Unhide',
      },
    },
  },
  relatedTags: {
    ...getBaseToolMessagesEN({
      singular: APP_FEATURE_NAMES_EN.relatedTags,
      plural: APP_FEATURE_NAMES_EN.relatedTags,
    }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      query: {
        label: 'Query',
        errors: {
          [PlayErrorReasonEnum.INVALID_FIELD]:
            'There are already related tags configured for this query',
        },
        help: 'Queries will be normalized. That means plurals and uppercases will be changed (e.g., "salads" to "salad").',
        normalizationText1: 'Your query',
        normalizationText2: 'has been normalized to',
      },
      lang: {
        label: 'Language',
        errors: {
          [PlayErrorReasonEnum.INVALID_FIELD]:
            'There are already related tags configured for this language',
        },
      },
      rules: {
        label: 'Related tags',
        columns: {
          added: {
            label: 'Curated',
          },
          removed: {
            label: 'Hidden organic',
          },
        },
        tag: {
          placeholder: 'tag',
          position: {
            [RelatedTagPositionEnum.BEFORE_QUERY]: 'Before',
            [RelatedTagPositionEnum.AFTER_QUERY]: 'After',
          },
        },
        actions: {
          remove: {
            show: 'Show turned off related tags',
            hide: 'Don’t show turned off related tags',
          },
        },
        validations: {
          duplicated: 'A curated related tag already exists.',
          matchesIndexed: 'An organic related tag already exists.',
        },
      },
      indexed: {
        label: 'Organic',
        disableAll: 'Turn off all organic related tags',
      },
    }),
    info: {
      scope: 'Changes will apply enterprise-wide.',
    },
    contextChange: {
      title: 'Context change',
      description: {
        query:
          'If you select another query, any changes for this configuration will be lost. Do you want to continue?',
        pivots:
          'If you select a different context, your curated tags will be saved, but the deactivated setup will be lost. Do you want to continue?',
      },
      actions: {
        confirm: 'Continue',
        cancel: 'Back',
      },
    },
    enable: {
      confirmTitle: 'Enable related tags',
      confirmMessage:
        'This action will turn off the configurations for the selected related tags that already have one on, do you want to continue?',
      success: 'Related tags configuration turned on successfully',
    },
  },
  nextQueries: {
    ...NEXT_QUERIES_BASE_MESSAGES_EN,
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      query: {
        label: 'Query',
      },
      rules: {
        label: 'Rules',
        types: {
          add: 'Curated',
          remove: 'Hidden organic',
        },
        validations: {
          containsQuery: '{query} cannot be used. Try another term.',
          duplicated: 'A curated next query already exists.',
          withoutResults: 'Unable to save. No results for this query.',
          matchesIndexed: 'An organic next query already exists.',
        },
        actions: {
          remove: {
            show: 'Show turned off next queries',
            hide: 'Don’t show turned off next queries',
          },
        },
      },
      indexed: {
        label: 'Organic',
        disableAll: 'Turn off all organic next queries',
      },
    }),
    results: '{counter} results',
    resultsWithPartial: '{partial} of {total} results shown',
    notAllPivotsSelected:
      'Select the filters for the context you want to use to create a Next Query configuration.',
    search: {
      placeholder: 'Type a query',
      resultsInfo: {
        some: 'Results for <b>{query}</b>',
        none: 'No results found for <b>{query}</b>',
      },
      help: 'Queries will be normalized. That means plurals and uppercases will be changed (e.g., "salads" to "salad").',
      normalizationText1: 'Your query',
      normalizationText2: 'has been normalized to',
    },
    nextQueries: 'Next queries',
    repeatedContext: {
      dialog: {
        ...NEXT_QUERIES_BASE_MESSAGES_EN.repeatedContext.dialog,
        confirmOld: 'Edit',
        content:
          'There is already an active next queries configuration for this scenario (context and query). What would you like to do?',
      },
    },
    contextChange: {
      dialog: {
        title: 'Context change',
        content:
          'If you select another query or context, any changes for this configuration will be lost. Do you want to continue?',
        confirm: 'Continue',
        deny: 'Back',
      },
    },
    enable: {
      confirmTitle: 'Enable next queries',
      confirmMessage:
        'This action will turn off the configurations for the selected next queries that already have one on, do you want to continue?',
      success: 'Next queries configuration turned on successfully',
    },
  },
  instances: {
    ...getBaseToolMessagesEN({ singular: 'Instance', plural: 'Instances' }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      instanceId: {
        label: 'ID',
      },
      prettyName: {
        label: 'Label',
      },
      imagotype: {
        label: 'Imagotype',
      },
      isotype: {
        label: 'Isotype',
      },
      permissions: {
        label: 'Permissions',
        feature: {
          label: 'Feature',
        },
        level: {
          label: 'Permission',
          values: {
            [PermissionLevelEnum.NONE]: 'None',
            [PermissionLevelEnum.READ]: 'Read',
            [PermissionLevelEnum.WRITE]: 'Write',
          },
          remove: 'Remove configuration',
        },
      },
      kpis: {
        label: 'KPIs',
        fields: {
          id: {
            label: 'KPI',
          },
        },
      },
      catalog: {
        label: 'Catalog',
        fields: {
          title: 'Fields',
          id: {
            label: 'Id',
          },
          prettyName: {
            label: 'Label',
          },
          types: {
            [CatalogFieldTypeEnum.SEARCHABLE]: 'Searchable',
            [CatalogFieldTypeEnum.RANKABLE]: 'Rankable',
            [CatalogFieldTypeEnum.FACETABLE]: 'Facetable',
          },
          actions: {
            add: 'Add field',
            edit: 'Edit field',
            confirm: 'Confirm changes',
            delete: 'Delete field',
          },
          noFieldsAddedYet: 'No fields added yet',
        },
        resultMappings: {
          title: 'Result mappings',
          [ResultMappingIdEnum.ID]: {
            label: 'ID',
          },
          [ResultMappingIdEnum.NAME]: {
            label: 'Name',
          },
          [ResultMappingIdEnum.URL]: {
            label: 'Url',
          },
          [ResultMappingIdEnum.URL_PREFIX]: {
            label: 'Url prefix',
          },
          [ResultMappingIdEnum.IMAGE_URL]: {
            label: 'Image url',
          },
          [ResultMappingIdEnum.IMAGE_URL_PREFIX]: {
            label: 'Image url prefix',
          },
          none: 'None',
        },
        index: {
          title: 'Index',
          token: 'Token',
        },
      },
      tools: {
        label: 'Tools',
        base: {
          label: 'Base',
          fields: {
            pivots: {
              label: 'Pivots',
            },
          },
        },
        [ActionTypeEnum.SYNONYM]: {
          label: 'Synonym',
          fields: {
            types: {
              label: 'Types',
              id: {
                label: 'Id',
              },
              prettyName: {
                label: 'Label',
              },
              actions: {
                add: 'Add type',
                edit: 'Edit type',
                confirm: 'Confirm changes',
                delete: 'Delete type',
              },
              noTypesAddedYet: 'No types added yet',
            },
          },
        },
        [ActionTypeEnum.CONFIGURATION]: {
          label: 'Configuration',
          fields: {
            attributeNames: {
              label: 'Attributes',
            },
          },
        },
      },
    }),
    instance: 'Instance',
  },
  index: {
    ...INDEX_BASE_MESSAGES_EN,
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_EN, {
      id: {
        label: 'Job ID',
      },
      clientId: {
        label: 'Instance',
      },
      feedId: {
        label: 'Feed Id',
      },
      state: {
        label: 'Status',
        values: {
          QUEUED: 'queued',
          RUNNING: 'running',
          SUCCESS: 'success',
          PARTIAL_SUCCESS: 'partial success',
          FAILURE: 'failure',
          DISCARDED: 'discarded',
        },
      },
      transformation: {
        label: 'Documents',
        stateLabel: 'transformation',
        read: 'R',
        write: 'W',
      },
      deployments: {
        label: 'Deployments',
        stateLabel: 'deployment',
      },
      deploymentId: {
        label: 'Deployment ID',
      },
      pivots: {
        label: 'Pivots',
      },
      created: {
        label: 'Date',
      },
      totalTime: {
        label: 'Time',
      },
      options: {
        discard: {
          text: 'Discard',
          success: 'Discarded successfully.',
          error: 'Job not discarded',
        },
        download: {
          text: 'Download file',
          success: 'Downloaded successfully.',
          error: 'Feed not downloaded',
        },
        reimport: {
          text: 'Reimport',
          success: 'Feed successfully queued to be reimported.',
          error: 'Job not reimported',
        },
      },
      file: {
        label: 'File',
        placeholder: 'Select a file',
      },
    }),
    details: {
      title: 'Job details {id}',
    },
    filtersAside: {
      applyFilters: 'Apply filters',
      title: 'Select filters',
    },
    list: {
      noDataFound: 'No jobs found',
      error: 'Index jobs not displayed',
    },
    noDataFound: 'There are no feeds yet',
    timeToReload: '{time}s until automatic reload',
    submitModal: {
      button: 'Submit job',
      title: 'Submit job',
      selectInstance: 'Select an instance',
      selectFeed: 'Select a feed',
      noReload: 'Do not reload the page until the file is uploaded.',
      success: 'Feed file submitted successfully.',
      error: "The feed couldn't be submitted.",
      actions: {
        close: 'Close',
      },
    },
    configModal: {
      button: 'Config job',
      title: 'Config job',
      selectInstance: 'Select an instance',
      selectFeed: 'Select a feed',
      success: 'Job configured successfully.',
      actions: {
        close: 'Close',
      },
    },
    newInstanceModal: {
      button: 'Create instance',
      title: 'Create new instance',
      form: {
        pluginId: {
          label: 'Plugin ID',
        },
        feeds: {
          label: 'Feeds',
        },
      },
      actions: {
        save: 'Save',
        cancel: 'Cancel',
      },
      success: 'Instance created successfully on the index service',
    },
    errors: {
      ...INDEX_BASE_MESSAGES_EN.errors,
      deployments: {
        partial: '{count} of {total} deployment(s) failed',
        total: 'All deployments failed',
      },
    },
  },
  facets: {
    ...getBaseToolMessagesEN({
      singular: APP_FEATURE_NAMES_EN.facets,
      plural: APP_FEATURE_NAMES_EN.facets,
    }),
    fields: {
      name: { ...DEFAULT_FIELDS_MESSAGES_EN.name },
      queries: {
        label: 'Queries',
      },
      endpointId: {
        label: 'Endpoint',
      },
      context: {
        label: 'Queries/Attributes',
      },
      matchAll: {
        label: 'Match all queries',
        value: 'All queries',
        help: 'This option will be applied to all searches of your site',
        enabled: 'On',
        disabled: 'Off',
      },
      user: { ...DEFAULT_FIELDS_MESSAGES_EN.user },
      updateDate: { ...DEFAULT_FIELDS_MESSAGES_EN.updateDate },
      enabled: { ...DEFAULT_FIELDS_MESSAGES_EN.enabled },
    },
    custom: {
      context: {
        label: 'Endpoint',
      },
      search: {
        label: 'Queries',
        help: "Add words separated by ';' or ',' and press the \"enter\" key",
      },
      attribute: {
        label: 'Attribute',
      },
      browse: {
        label: 'Attribute value',
        filters: {
          title: 'Select attribute filters',
          subtitle: 'These filters are only used to load attribute values.',
          accept: 'Apply filters',
        },
        errors: {
          noValues: 'There are no values available for this attribute',
        },
      },
      inheritance: {
        label: 'Select all child values',
      },
    },
    settings: {
      action: 'Add',
      modal: {
        title: 'Set-up',
        confirmButton: 'Save',
        facets: {
          label: 'Facets',
          placeholder: 'Select facets',
        },
      },
    },
    loading: 'Loading...',
    contextChange: {
      dialog: {
        title: 'Context change',
        content:
          'If you select another context, any changes for this configuration will be lost. Do you want to continue?',
        confirm: 'Continue',
        deny: 'Back',
      },
    },
    errorEnabled: {
      title: 'Duplicated items',
      description:
        'You are trying to turned on multiple settings with the same configuration at once.',
    },
    enable: {
      confirmTitle: 'Enable facet',
      confirmMessage:
        'This action will turn off other active facet that are in conflict with the selected one. Do you want to continue?',
      success: 'Facet configuration turned on successfully',
    },
    showMore: 'Show more',
    aside: {
      ...BASE_ASIDE_MESSAGES_EN,
      ...BASE_ASIDE_FACETS_MESSAGES_EN,
      header: 'Set up your facet',
    },
    asideForEdit: {
      ...BASE_ASIDE_MESSAGES_EN,
      ...BASE_ASIDE_FACETS_MESSAGES_EN,
      header: 'Edit your facet',
    },
  },
  relatedPrompts: {
    landing: {
      title: 'Related Prompts',
    },
    search: {
      placeholder: 'Query',
    },
    overview: {
      title: 'Overview',
      table: {
        name: 'Related Prompt',
        id: 'ID',
        language: 'Language',
        nextQueries: 'Next Queries',
        state: 'Status',
      },
    },
    filters: {
      title: 'Select filters',
      accept: 'Apply filters',
    },
    preview: {
      title: 'Preview',
      nextQuery: {
        warning: {
          tooltip:
            "This query is identical to your current search and won't show up in the search results.",
        },
      },
    },
    metrics: {
      queries: 'Queries with related prompts last 7 days.',
      clicks: 'Clicks gained last 7 days.',
      add2cart: 'Add2cart gained last 7 days.',
    },
  },
}

// =====================================
//
//               SPANISH
//
// =====================================

const DEFAULT_FIELDS_MESSAGES_ES = {
  id: {
    label: 'ID',
  },
  name: {
    label: 'Nombre',
  },
  creationDate: {
    label: 'Creado el',
  },
  updateDate: {
    label: 'Actualizado el',
  },
  user: {
    label: 'Actualizado por',
  },
  enabled: {
    label: 'Estado',
    states: {
      enabled: 'Encendido',
      disabled: 'Apagado',
    },
  },
  schedule: {
    label: 'Programado',
    states: {
      schedule: 'Programado',
      activationDate: 'Desde el {date}',
      deactivationDate: 'Hasta el {date}',
      always: 'Siempre activo',
    },
  },
  placeholders: {
    label: '', // TODO: remove placeholders from FIELD_MESSAGES
    always: 'Siempre',
    scheduled: 'Selecciona una fecha',
    keywords: {
      active: "Añade palabras separadas por ';' o ',' y presiona la tecla \"intro\"",
      disabled: 'Las queries están inactivas para este link',
    },
  },
  activationDate: {
    label: 'Activo desde',
  },
  deactivationDate: {
    label: 'Activo hasta',
  },
}

const TOOLING_PREVIEW_MESSAGES_ES: ToolingPreviewMessages = {
  title: 'Vista previa',
  search: 'Escribe una query',
  filters: {
    // TODO: translate
    title: 'Select preview filters',
    subtitle:
      'If you select preview filters you can see how users will view the result grid with there filters applied.',
    accept: 'Apply filters',
  },
  results: '{counter} resultados',
  noDataFound: {
    title: "D'oh!",
    description: 'No se han encontrado resultados',
  },
  emptyQuerySearch: {
    // TODO: translate
    text1: 'Type a query and select preview filter',
    text2: 'to start the products preview.',
  },
}

const BASE_ASIDE_MESSAGES_ES: Partial<AsidePanelMessages> = {
  actions: {
    clear: 'Limpiar',
    accept: 'Guardar',
    cancel: 'Cancelar',
    add: 'Añadir',
  },
}

const BASE_ASIDE_FACETS_MESSAGES_ES: AsidePanelFacetsMessages = {
  sortType: {
    values: {
      [FacetSortTypeEnum.ALPHABETICALLY]: '',
      [FacetSortTypeEnum.RESULTS_NUMBER]: '',
      custom: '',
    },
  },
  labels: {
    facets: '',
    name: '',
    sortValues: '',
    unit: '',
    decimalDigits: '',
  },
  metadata: {
    title: '',
    description: '',
    labels: {
      field: '',
      value: '',
    },
    addValue: '',
    editValue: '',
    error: '',
  },
}

const CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES =
  'los cambios serán aplicados en el siguiente ciclo de indexación del catálogo'

export const getBaseToolMessagesES = (
  toolName = { singular: 'Configuración', plural: 'Configuraciones' },
  indexingWarn = false,
): BaseToolMessages => {
  const indexingWarnMessage = indexingWarn ? `; ${CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES}` : ''
  return {
    actions: {
      accept: 'Guardar',
      cancel: 'Cancelar',
      exit: 'Atrás',
    },
    noDataFound: `No hemos podido encontrar ${toolName.plural.toLowerCase()} que se ajusten a la búsqueda.`,
    dataFiltered: ` Prueba a eliminar algunos filtros y buscar de nuevo.`,
    deleteMany: {
      confirmTitle: `Eliminar ${toolName.plural.toLowerCase()}`,
      confirmMessage:
        '¿Estás seguro de que quieres eliminar los elementos seleccionados ({itemsCount})?',
      success: `${toolName.plural} eliminados con éxito${indexingWarnMessage}`,
    },
    deleteOne: {
      confirmTitle: `Eliminar ${toolName.singular.toLowerCase()}`,
      confirmMessage: '¿Estás seguro de que quieres eliminar el elemento seleccionado?',
      success: `${toolName.singular} eliminado con éxito${indexingWarnMessage}`,
    },
    sections: {
      details: {
        title: 'Detalles',
      },
      filters: {
        title: 'Filtros',
        info: 'Seleccione los filtros que desea aplicar para crear un escenario personalizado para su configuración',
      },
      settings: {
        title: 'Configuración',
      },
      preview: {
        title: 'Vista previa',
      },
    },
    errors: {
      enableConflictError: {
        title: 'Configuraciones solapadas encontradas',
        message:
          'Algunas configuraciones seleccionadas no han podido ser activadas porque están en conflicto con otras (<strong>{conflictingElements}</strong>).',
      },
    },
    create: {
      success: `${toolName.singular} creado con éxito${indexingWarnMessage}`,
    },
    update: {
      success: `${toolName.singular} editado con éxito${indexingWarnMessage}`,
    },
    enable: {
      success: `${toolName.plural} activados con éxito${indexingWarnMessage}`,
    },
    disable: {
      success: `${toolName.plural} desactivados con éxito${indexingWarnMessage}`,
    },
    repeatedContext: {
      dialog: {
        title: 'Configuraciones solapadas encontradas',
        content:
          'No se puede activar y guardar la configuración actual porque hay algunos conflictos. Aquí está la lista de configuraciones solapadas:',
        detail: ' con las queries: {queries}',
        options: {
          title: 'Cómo proceder?',
          disablePrevious: 'Apagar la configuración existente y guardar esta como encendida.',
          disableActual: 'Guardar la configuración actual como desactivada',
        },
        confirm: 'Guardar',
        deny: 'Cancelar',
      },
    },
  }
}
const APP_FEATURE_NAMES_ES = APP_MESSAGES[LocaleEnum.SPANISH].app.features
const INDEX_BASE_MESSAGES_ES = getBaseToolMessagesES({ singular: 'Index', plural: 'Indexes' })
const NEXT_QUERIES_BASE_MESSAGES_ES = getBaseToolMessagesES({
  singular: APP_FEATURE_NAMES_ES.nextQueries,
  plural: APP_FEATURE_NAMES_ES.nextQueries,
})

const TOOLS_MESSAGES_ES: ToolsMessages = {
  equalize: {
    ...getBaseToolMessagesES({
      singular: APP_FEATURE_NAMES_ES.equalize,
      plural: APP_FEATURE_NAMES_ES.equalize,
    }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      title: {
        label: 'Nombre',
        defaultDescription: '',
      },
      notes: {
        label: 'Notas',
        actions: {
          show: 'Mostrar notas',
          hide: 'Ocultar notas',
        },
      },
      endpointId: {
        label: 'Endpoint',
        dialog: {
          title: 'Cambio de contexto',
          content:
            'Si seleccionas otro contexto, cualquier cambio para esta configuración se perderá. ¿Quieres continuar?',
          confirm: 'Continuar',
          deny: 'Atrás',
        },
      },
      modified: {
        label: 'Modificado',
      },
      enabledTime: {
        label: 'Tiempo activo',
      },
      enabled: {
        ...DEFAULT_FIELDS_MESSAGES_ES.enabled,
        alert: 'Habilitar este equalize cambiará la configuración actual del buscador',
      },
      search: {
        label: 'Busca productos para previsualizar',
        help: 'Busca un término para visualizar los resultados de la configuración del equalize',
      },
    }),
    products: {
      countInfo: 'Mostrando {counter} resultados',
    },
    enable: {
      confirmTitle: 'Habilitar equalize',
      confirmMessage:
        'Esta acción inactivara el equalize actual, ¿estás seguro de que quieres hacerlo?',
      success: 'Equalize activo con éxito',
    },
    features: {
      defaultButton: 'Configuración por defecto',
      setByDefault: 'Establecido por defecto',
      multiMatchSearch: {
        title: 'Textual match',
        description: '',
      },
      semanticMatchSearch: {
        title: 'Semantic match',
        description: '',
        disabledDescription: '',
      },
      boosting: {
        title: 'Global business rules',
        description: '',
        audit: {
          field: '',
          value: '',
          action: '',
          boostFactor: '',
        },
      },
      functionScore: {
        title: 'Function score',
        description: '',
        audit: {
          fieldName: '',
          formulaType: '',
          weight: '',
          boostFactor: '',
        },
      },
      sponsoredScore: {
        title: 'Monetization',
        description: '',
        disabledDescription: '',
        audit: {
          fieldName: '',
          formulaType: '',
          weight: '',
          boostFactor: '',
        },
      },
    },
    details: 'Detalles',
    configuration: 'Configuración',
    fieldValues: 'Campos',
    preview: {
      ...TOOLING_PREVIEW_MESSAGES_ES,
      browseField: {
        label: 'Atributo',
      },
      browseValue: {
        label: 'Valor del atributo',
        filters: {
          title: 'Seleccionar filtros de atributo',
          subtitle: 'Estos filtros sólo se utilizan para cargar valores de atributos.',
          accept: 'Aplicar filtros',
        },
        errors: {
          noValues: 'No hay valores disponibles para este atributo',
        },
      },
    },
    results: '{counter} resultados',
    conflictErrorList: {
      title: '',
      message: '',
    },
    conflictErrorForm: {
      title: '',
      message: '',
      continue: '',
    },
  },
  synonyms: {
    ...getBaseToolMessagesES({ singular: 'Sinónimo', plural: 'Sinónimos' }, true),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      id: {
        label: '',
      },
      hyponyms: {
        label: 'Hipónimos',
        placeholder: '',
        help: 'Cada uno de estos términos devolverá lo mismo que sus sinónimos equivalentes',
        valuesCountInfo: '{counter} hipónimos añadidos',
      },
      synonyms: {
        label: 'Sinónimos',
        placeholder: '',
        help: 'Todos estos términos significarán lo mismo para el motor de búsqueda',
        valuesCountInfo: '{counter} sinónimos añadidos',
        errors: {
          [PlayErrorReasonEnum.DUPLICATED]:
            'Valores en conflicto ({values}) con el sinónimo [{synonym}]',
        },
      },
      type: {
        label: 'Tipo',
        values: {
          color: 'Color',
          basic: 'Básico',
        },
      },
    }),
    noValues: 'Aún no se han añadidos sinónimos',
    return: 'También devuelve',
    conflicts: {
      disable: {
        success: `Sinónimo en conflicto inactivo con éxito; ${CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES}`,
      },
      delete: {
        confirmTitle: 'Eliminar sinónimo',
        confirmMessage: '¿Estás seguro de que quieres eliminar el sinónimo en conflicto?',
        success: `Sinónimo en conflicto eliminado con éxito; ${CHANGES_APPLIED_WHEN_INDEXING_MESSAGE_ES}`,
      },
    },
    suggestions: {
      title: '',
      help: '',
    },
    reminder: {
      heuristic: '',
    },
  },
  blacklist: {
    ...getBaseToolMessagesES({ singular: APP_FEATURE_NAMES_ES.blacklist, plural: 'Blacklists' }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      lang: {
        label: 'Idioma',
        errors: {
          [PlayErrorReasonEnum.INVALID_FIELD]: 'Ya existe una blacklist activada para este idioma',
        },
      },
      terms: {
        label: 'Términos',
        info: 'Lista de términos asociados a la blacklist',
      },
    }),
  },
  promotions: {
    ...getBaseToolMessagesES({ singular: 'Promoción', plural: 'Promociones' }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      name: {
        label: 'Nombre',
      },
      keywords: {
        label: 'Queries',
        info: 'Lista de términos de búsqueda para los que la promotion se muestra',
        valuesCountInfo: '{counter} queries añadidas',
      },
      matchAll: {
        label: 'Mostrar en todas las búsquedas',
        value: 'Todas las búsquedas',
        help: 'Esta opción será aplicada a todas las búsquedas del sitio',
        enabled: 'On',
        disabled: 'Off',
      },
      clicks: {
        label: 'Clicks',
      },
      alertDevicesTOPs: {
        label: '',
      },
    }),
    aside: {
      ...BASE_ASIDE_MESSAGES_ES,
      header: 'Configura tu promotion',
      type: {
        label: 'Tipo',
        values: {
          [PromotionTypeEnum.BANNER]: 'Banner',
          [PromotionTypeEnum.PROMOTED]: 'Promoted',
        },
      },
      position: {
        label: 'Posición',
        formLabel: {
          [PromotionTypeEnum.BANNER]: 'Fila',
          [PromotionTypeEnum.PROMOTED]: 'Posición',
        },
      },
      url: {
        label: 'URL',
        help: 'Introduce la URL de la promotion. Usa una URL relative (ej. /diadelamadre) solo si es compatible con tu implementación.',
        error: 'La URL debe ser válida.',
      },
      imageUrl: {
        label: 'Imagen',
        previewLabel: 'Vista previa de la imagen',
        help: '',
        info: '',
        error: 'La URL debe ser válida.',
      },
      title: {
        label: 'Título',
        errors: {
          empty: 'El título no puede estar vacío',
          length: 'El título es demasiado largo',
        },
      },
    },
    settings: {
      emptyCard: 'Sin título',
      error: 'Existe algún error dentro de esta configuración',
    },
    preview: {
      noResults: 'Oops! Something went wrong while loading the preview.',
      cardType: {
        desktop: 'Desktop',
        mobile: 'Mobile',
      },
    },
  },
  redirections: {
    ...getBaseToolMessagesES({
      singular: APP_FEATURE_NAMES_ES.redirection,
      plural: 'Redirections',
    }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      keywords: {
        label: 'Queries',
        info: 'Lista de términos de búsqueda para los que la redirection se muestra',
        valuesCountInfo: '{counter} queries añadidas',
      },
      url: {
        label: 'URL',
        help: 'Introduce la URL de la redirection. Usa una URL relative (ej. /diadelamadre) solo si es compatible con tu implementación.',
      },
    }),
  },
  configurations: {
    ...getBaseToolMessagesES({
      singular: APP_FEATURE_NAMES_ES.configuration.browse,
      plural: 'Configuraciones',
    }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      action: {
        label: 'Acción',
      },
      restricted: {
        label: 'Restringido',
        roles: {
          default: 'Todos',
        },
        values: {
          [RestrictedValueEnum.READ]: '<b>LEER:<b/> {roles}',
          [RestrictedValueEnum.WRITE]: '<b>ESCRIBIR:</b> {roles}',
        },
      },
      endpointId: {
        label: 'Contexto',
        values: {
          search: {
            label: 'Búsqueda',
          },
          browse: {
            label: 'Navegación',
          },
        },
        dialog: {
          title: 'Cambio de contexto',
          content:
            'Si selecciona otro contexto, se perderán los cambios realizados en esta configuración. ¿Desea continuar?',
          confirm: 'Continuar',
          deny: 'Volver',
        },
      },
      queriesAttribute: {
        label: 'Queries/Atributos',
      },
      keywords: {
        label: 'Queries',
        info: 'Lista de términos de búsqueda para los que la configuración se aplica',
      },
      browseField: {
        label: 'Atributo',
      },
      browseValue: {
        label: 'Valor del atributo',
        filters: {
          title: '',
          subtitle: '',
          accept: '',
        },
        errors: {
          noValues: '',
        },
      },
      features: {
        label: 'Modificado',
        attributes: 'atributos',
        hiddenAttributes: 'esconder atributos',
        products: 'productos',
        pinnedProducts: 'pins',
        hiddenProducts: 'productos escondidos',
        facets: 'facets',
      },
      facets: {
        label: 'Facets',
        info: 'Selecciona el grupo de facets y valores a mostrar en la página de listado de productos.',
        edit: 'Seleccionar facets',
        id: {
          label: 'Facet',
        },
        prettyName: {
          label: 'Nombre',
        },
        sortType: {
          label: 'ordenar valores',
          inlineLabel: 'ordenado',
          values: {
            default: 'por defecto',
            [FacetSortTypeEnum.ALPHABETICALLY]: 'alfabéticamente',
            [FacetSortTypeEnum.RESULTS_NUMBER]: 'por resultados',
          },
        },
        values: {
          empty: 'No hay ningún valor configurado.',
        },
        actions: {
          add: 'Añadir Facet',
          preview: 'Mostrar facets',
        },
        emptyList: 'No hay ningún facet configurado.',
      },
      attributes: {
        name: {
          label: 'Atributo',
        },
        value: {
          label: 'Valor',
          filters: {
            title: '',
            subtitle: '',
            accept: '',
          },
          errors: {
            noValues: '',
          },
        },
        label: 'Atributos',
        info: 'Haz boost o bury a ciertos valores de atributos de tu catálogo.',
        edit: 'Seleccionar atributos',
        fieldName: 'Atributo',
        fieldValue: 'Valor',
        noValues: '',
        actions: {
          bury: 'Bury',
          boost: 'Boost',
        },
        emptyList: 'No hay ningún atributo configurado.',
        setupRanking: '',
        editRankingTitle: 'Editar ranking de atributo',
      },
      hiddenAttributes: {
        label: 'Atributos escondidos',
        emptyList: 'No hay attributos escondidos',
        setupRanking: 'Esconder un atributo',
      },
      products: {
        label: 'Productos',
        info: 'Selecciona en la vista previa los productos a los que quieras hacer boost o bury. Aparecerán listados aquí.',
        emptyList: 'No hay ningún producto configurado.',
        setupRanking: '',
        invalidProductID: '',
        search: {
          tag: 'Query',
          query: 'Buscar productos',
          help: 'Busca un producto usando una query o un ID',
        },
        notFound: 'No se han encontrado productos',
        editRankingTitle: 'Editar ranking de producto',
      },
      pinnedProducts: {
        label: 'Productos pineados',
        emptyList: 'No hay productos pineados',
        setupRanking: 'Pinear un producto',
      },
      hiddenProducts: {
        label: 'Productos escondidos',
        info: 'Selecciona en la vista previa los productos que quiere esconder. Aparecerán listados aquí.',
        emptyList: 'No hay productos escondidos',
        setupRanking: 'Esconder un producto',
        invalidProductID: '',
        search: {
          tag: 'Query',
          query: 'Buscar productos',
          help: 'Busca un producto usando una query o un ID',
        },
        notFound: 'No se han encontrado productos',
        editRankingTitle: 'Editar producto escondido',
      },
      inheritance: {
        label: 'Herencia',
        inputLabel: 'Aplicar configuración a los elementos hijos',
        states: {
          enabled: 'Sí',
          disabled: 'No',
        },
      },
      elements: {
        label: 'Elementos',
      },
    }),
    enable: {
      confirmTitle: '',
      confirmMessage: '',
      success: '',
    },
    attributes: {
      names: {
        category: 'categorías',
      },
      noDataFound: 'No se han encontrado {attributeName}',
    },
    configurations: 'configuraciones',
    results: '{counter} resultados',
    removeAll: {
      label: 'Eliminar todo',
      title: 'Eliminar todas las configuraciones',
      description:
        '¿Estás seguro de que quieres eliminar todas las configuraciones de esta sección?',
      actions: {
        confirm: 'Eliminar',
        cancel: 'Cancelar',
      },
    },
    modals: {
      attributes: {
        title: 'Ranking por atributos',
        overlappedResult: '',
      },
      hiddenAttributes: {
        title: 'Atributos escondidos',
        overlappedResult: '',
      },
      products: {
        title: 'Ranking de productos',
        fields: {
          products: {
            label: 'Productos',
            placeholder: 'Introduce un ID de producto',
            description: 'Introduce un ID de producto separado por punto y coma y pulse Intro',
          },
        },
        overlappedResult: '',
      },
      pinnedProducts: {
        title: 'Pinear producto',
        info: 'La posición final de este producto puede verse afectada por otras configuraciones',
        fields: {
          productId: {
            label: 'ID de producto',
          },
          position: {
            label: 'Posición',
            info: 'Introduce una posición entre {min} y {max}',
          },
        },
        overlappedResult: '',
        positionInUse: '',
      },
      hiddenProducts: {
        title: 'Productos escondidos',
        fields: {
          products: {
            label: 'Productos',
            placeholder: 'Introduce un ID de producto',
            description: 'Introduce un ID de producto separado por punto y coma y pulse Intro',
          },
        },
        overlappedResult: '',
      },
    },
    preview: TOOLING_PREVIEW_MESSAGES_ES,
    quickRanking: {
      title: 'Quick boost',
      resultsLabel: 'Introduce un ID de producto',
      resultsDescription: 'Introduce un ID de producto separado por punto y coma y pulse Intro',
      actions: {
        [QueryRankingElementRankingAction.BOOST]: 'Boost',
        [QueryRankingElementRankingAction.BURY]: 'Bury',
      },
      overlappedResult: 'Producto con conflictos en filtros y/o horario',
    },
    status: {
      [QueryRankingElementRankingAction.BOOST]: 'Boosted',
      [QueryRankingElementRankingAction.BURY]: 'Buried',
      [PinProductAction]: 'Pinned',
      [HiddenElementsAction]: 'Hidden',
    },
    actionLabels: {
      [QueryRankingElementRankingAction.BURY]: {
        positive: 'Bury',
        positivePast: 'Buried',
        negative: 'Unbury',
      },
      [QueryRankingElementRankingAction.BOOST]: {
        positive: 'Boost',
        positivePast: 'Boosted',
        negative: 'Unboost',
      },
      [PinProductAction]: {
        positive: 'Pin',
        positivePast: 'Pinned',
        negative: 'Unpin',
      },
      [HiddenElementsAction]: {
        positive: 'Hide',
        positivePast: 'Hidden',
        negative: 'Unhide',
      },
    },
  },
  relatedTags: {
    ...getBaseToolMessagesES({
      singular: APP_FEATURE_NAMES_ES.relatedTags,
      plural: APP_FEATURE_NAMES_ES.relatedTags,
    }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      query: {
        label: 'Query',
        errors: {
          [PlayErrorReasonEnum.INVALID_FIELD]:
            'Ya existen related tags configuradas para esta query',
        },
        help: '',
        normalizationText1: '',
        normalizationText2: '',
      },
      lang: {
        label: 'Idioma',
        errors: {
          [PlayErrorReasonEnum.INVALID_FIELD]:
            'Ya existen related tags configuradas para este idioma',
        },
      },
      rules: {
        label: 'Related tags',
        columns: {
          added: {
            label: 'Personalizadas',
          },
          removed: {
            label: 'Orgánicas ocultas',
          },
        },
        tag: {
          placeholder: 'tag',
          position: {
            [RelatedTagPositionEnum.BEFORE_QUERY]: 'Antes de',
            [RelatedTagPositionEnum.AFTER_QUERY]: 'Después de',
          },
        },
        actions: {
          remove: {
            show: 'Mostrar related tags ocultas',
            hide: 'No mostrar related tags ocultas',
          },
        },
        validations: {
          duplicated: '',
          matchesIndexed: '',
        },
      },
      indexed: {
        label: '',
        disableAll: '',
      },
    }),
    info: {
      scope: 'Los cambios aplicarán a nivel general',
    },
    contextChange: {
      title: 'Cambio de contexto',
      description: {
        query:
          'Si utilizas otra query o contexto, todos los cambios de esta configuración se perderán. ¿Quieres continuar?',
        pivots:
          'Si utilizas otra query o contexto, todos los cambios de esta configuración se perderán. ¿Quieres continuar?',
      },
      actions: {
        confirm: 'Continuar',
        cancel: 'Volver',
      },
    },
    enable: {
      confirmTitle: 'Habilitar related tags',
      confirmMessage:
        'Esta acción inactivara las related tags seleccionadas que ya estén activas, ¿desea continuar?',
      success: 'Related tags activadas con éxito',
    },
  },
  nextQueries: {
    ...NEXT_QUERIES_BASE_MESSAGES_ES,
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      query: {
        label: 'Query',
      },
      rules: {
        label: 'Reglas',
        types: {
          add: 'Personalizadas',
          remove: 'Orgánicas ocultas',
        },
        validations: {
          containsQuery: 'No se puede utilizar {query}. Prueba con otro término.',
          duplicated: 'Ya existe una next query configurada.',
          withoutResults: 'No se puede guardar. No existen resultados para esta query.',
          matchesIndexed: 'Ya existe una next query orgánica.',
        },
        actions: {
          remove: {
            show: '',
            hide: '',
          },
        },
      },
      indexed: {
        label: 'Orgánicas',
        disableAll: '',
      },
    }),
    results: 'Resultados',
    resultsWithPartial: '',
    notAllPivotsSelected:
      'Selecciona los filtros de contexto para crear una configuración de Next Queries.',
    search: {
      placeholder: 'Escribe una query',
      resultsInfo: {
        some: 'Resultados para <b>{query}</b>',
        none: 'No se han encontrado resultados para <b>{query}</b>',
      },
      help: '',
      normalizationText1: '',
      normalizationText2: '',
    },
    nextQueries: 'Next queries',
    repeatedContext: {
      dialog: {
        ...NEXT_QUERIES_BASE_MESSAGES_ES.repeatedContext.dialog,
        confirmOld: 'Editar',
        content:
          'Ya existe una configuración de next queries activa para este escenario (contexto y query). Qué te gustaría hacer?',
      },
    },
    contextChange: {
      dialog: {
        title: 'Cambio de contexto',
        content:
          'Si utilizas otra query o contexto, todos los cambios de esta configuración se perderán. ¿Quieres continuar?',
        confirm: 'Continuar',
        deny: 'Volver',
      },
    },
    enable: {
      confirmTitle: 'Habilitar next queries',
      confirmMessage:
        'Esta acción desactivará las next queries seleccionadas que ya estén activas, ¿desea continuar?',
      success: 'Next queries activadas con éxito',
    },
  },
  instances: {
    ...getBaseToolMessagesES({ singular: 'Instancia', plural: 'Instancias' }),
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      instanceId: {
        label: 'ID',
      },
      prettyName: {
        label: 'Etiqueta',
      },
      imagotype: {
        label: 'Imagotipo',
      },
      isotype: {
        label: 'Isotipo',
      },
      permissions: {
        label: 'Permisos',
        feature: {
          label: 'Feature',
        },
        level: {
          label: 'Permiso',
          values: {
            [PermissionLevelEnum.NONE]: 'Ninguno',
            [PermissionLevelEnum.READ]: 'Lectura',
            [PermissionLevelEnum.WRITE]: 'Escritura',
          },
          remove: 'Eliminar configuración',
        },
      },
      kpis: {
        label: 'KPIs',
        fields: {
          id: {
            label: 'KPI',
          },
        },
      },
      catalog: {
        label: 'Catálogo',
        fields: {
          title: 'Campos',
          id: {
            label: 'Id',
          },
          prettyName: {
            label: 'Etiqueta',
          },
          types: {
            [CatalogFieldTypeEnum.SEARCHABLE]: 'Searchable',
            [CatalogFieldTypeEnum.RANKABLE]: 'Rankable',
            [CatalogFieldTypeEnum.FACETABLE]: 'Facetable',
          },
          actions: {
            add: 'Añadir campo',
            edit: 'Editar campo',
            confirm: 'Confirmar cambios',
            delete: 'Borrar campo',
          },
          noFieldsAddedYet: 'Aún no se han añadido campos',
        },
        resultMappings: {
          title: 'Mapeos del resultado',
          [ResultMappingIdEnum.ID]: {
            label: 'ID',
          },
          [ResultMappingIdEnum.NAME]: {
            label: 'Nombre',
          },
          [ResultMappingIdEnum.URL]: {
            label: 'Url',
          },
          [ResultMappingIdEnum.URL_PREFIX]: {
            label: 'Prefijo de la url',
          },
          [ResultMappingIdEnum.IMAGE_URL]: {
            label: 'Url de la imagen',
          },
          [ResultMappingIdEnum.IMAGE_URL_PREFIX]: {
            label: 'Prefijo de la imagen',
          },
          none: 'Ninguno',
        },
        index: {
          title: 'Index',
          token: 'Token',
        },
      },
      tools: {
        label: 'Herramientas',
        base: {
          label: 'Base',
          fields: {
            pivots: {
              label: 'Pivotes',
            },
          },
        },
        [ActionTypeEnum.SYNONYM]: {
          label: 'Synonym',
          fields: {
            types: {
              label: 'Tipos',
              id: {
                label: 'Id',
              },
              prettyName: {
                label: 'Etiqueta',
              },
              actions: {
                add: 'Añadir tipo',
                edit: 'Editar tipo',
                confirm: 'Confirmar cambios',
                delete: 'Borrar tipo',
              },
              noTypesAddedYet: 'Aún no se han añadido tipos',
            },
          },
        },
        [ActionTypeEnum.CONFIGURATION]: {
          label: 'Configuration',
          fields: {
            attributeNames: {
              label: 'Atributos',
            },
          },
        },
      },
    }),
    instance: 'Instancia',
  },
  index: {
    ...INDEX_BASE_MESSAGES_ES,
    fields: Object.assign({}, DEFAULT_FIELDS_MESSAGES_ES, {
      id: {
        label: 'ID del Job',
      },
      clientId: {
        label: '',
      },
      feedId: {
        label: 'ID del Feed',
      },
      state: {
        label: 'Estado',
        values: {
          QUEUED: '',
          RUNNING: '',
          SUCCESS: '',
          PARTIAL_SUCCESS: '',
          FAILURE: '',
          DISCARDED: '',
        },
      },
      transformation: {
        label: '',
        stateLabel: '',
        read: 'L',
        write: 'E',
      },
      deployments: {
        label: '',
        stateLabel: '',
      },
      deploymentId: {
        label: '',
      },
      pivots: {
        label: '',
      },
      created: {
        label: 'Fecha',
      },
      totalTime: {
        label: 'Tiempo',
      },
      options: {
        discard: {
          text: '',
          success: '',
          error: '',
        },
        download: {
          text: '',
          success: '',
          error: '',
        },
        reimport: {
          text: '',
          success: '',
          error: '',
        },
      },
      file: {
        label: 'Archivo',
        placeholder: 'Selecciona un archivo',
      },
    }),
    details: {
      title: '',
    },
    filtersAside: {
      applyFilters: '',
      title: '',
    },
    list: {
      noDataFound: 'No se han encontrado jobs',
      error: '',
    },
    noDataFound: 'No se han encontrado jobs',
    timeToReload: '',
    submitModal: {
      button: 'Enviar job',
      title: 'Enviar job',
      selectInstance: '',
      selectFeed: 'Selecciona un feed',
      noReload: '',
      success: 'Feed enviado correctamente.',
      error: '',
      actions: {
        close: 'Cerrar',
      },
    },
    configModal: {
      button: 'Configurar job',
      title: 'Configurar job',
      selectInstance: 'Selecciona una instancia',
      selectFeed: 'Selecciona un feed',
      success: 'Job configurado correctamente.',
      actions: {
        close: 'Cerrar',
      },
    },
    newInstanceModal: {
      button: 'Crear instancia',
      title: 'Crear instancia',
      form: {
        pluginId: {
          label: 'ID del plugin',
        },
        feeds: {
          label: 'Feeds',
        },
      },
      actions: {
        save: 'Guardar',
        cancel: 'Cancelar',
      },
      success: 'Instancia creada correctamente en el servicio de indexación',
    },
    errors: {
      ...INDEX_BASE_MESSAGES_ES.errors,
      deployments: {
        partial: '',
        total: '',
      },
    },
  },
  facets: {
    ...getBaseToolMessagesES({
      singular: APP_FEATURE_NAMES_ES.facets,
      plural: APP_FEATURE_NAMES_ES.facets,
    }),
    fields: {
      name: { ...DEFAULT_FIELDS_MESSAGES_ES.name },
      queries: {
        label: '',
      },
      endpointId: {
        label: '',
      },
      context: {
        label: '',
      },
      matchAll: {
        label: '',
        value: '',
        help: '',
        enabled: '',
        disabled: '',
      },
      user: { ...DEFAULT_FIELDS_MESSAGES_ES.user },
      updateDate: { ...DEFAULT_FIELDS_MESSAGES_ES.updateDate },
      enabled: { ...DEFAULT_FIELDS_MESSAGES_ES.enabled },
    },
    custom: {
      context: {
        label: '',
      },
      search: {
        label: '',
        help: '',
      },
      attribute: {
        label: '',
      },
      browse: {
        label: '',
        filters: {
          title: '',
          subtitle: '',
          accept: '',
        },
        errors: {
          noValues: '',
        },
      },
      inheritance: {
        label: '',
      },
    },
    settings: {
      action: '',
      modal: {
        title: '',
        confirmButton: '',
        facets: {
          label: '',
          placeholder: '',
        },
      },
    },
    loading: '',
    contextChange: {
      dialog: {
        title: '',
        content: '',
        confirm: '',
        deny: '',
      },
    },
    errorEnabled: {
      title: '',
      description: '',
    },
    enable: {
      confirmTitle: '',
      confirmMessage: '',
      success: '',
    },
    showMore: '',
    aside: {
      ...BASE_ASIDE_MESSAGES_ES,
      ...BASE_ASIDE_FACETS_MESSAGES_ES,
      header: '',
    },
    asideForEdit: {
      ...BASE_ASIDE_MESSAGES_ES,
      ...BASE_ASIDE_FACETS_MESSAGES_ES,
      header: '',
    },
  },
  relatedPrompts: {
    landing: {
      title: '',
    },
    search: {
      placeholder: '',
    },
    overview: {
      title: '',
      table: {
        name: '',
        id: '',
        language: '',
        nextQueries: '',
        state: '',
      },
    },
    filters: {
      title: '',
      accept: '',
    },
    preview: {
      title: '',
      nextQuery: {
        warning: {
          tooltip: '',
        },
      },
    },
    metrics: {
      queries: '',
      clicks: '',
      add2cart: '',
    },
  },
}

export const TOOLS_MESSAGES: Dictionary<{ tools: ToolsMessages }> = {
  [LocaleEnum.ENGLISH]: {
    tools: TOOLS_MESSAGES_EN,
  },
  [LocaleEnum.SPANISH]: {
    tools: TOOLS_MESSAGES_ES,
  },
}
